import React, { useMemo, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Dimensions,
  Platform,
  type StyleProp,
  type ViewStyle,
} from 'react-native';
import { BarChart } from 'react-native-gifted-charts';
import { useTheme, GTFonts, PoppinsFonts } from '../../theme';
import {
  capitalizeFirstLetter,
  fs,
  s,
  v,
  formatMoney,
} from '../../utils/libs';
import { formatNiceAxisTick, getNiceAxis } from '../../utils/chartAxis';
import {
  getExpenseCategoryColor,
  getExpenseTypeOrder,
} from '../../utils/expenseCategories';
import ChartTooltip from './ChartTooltip';
import PositionedChartTooltip from './PositionedChartTooltip';
import {
  estimateChartTooltipHeight,
  getBarChartPointX,
  getBarChartTooltipTranslateY,
  getBarHeightFromValue,
} from './chartTooltipPosition';

type AnnualPoint = {
  month: string;
  totalsByType: Record<string, number>;
  total: number;
};

type Props = {
  data: AnnualPoint[];
  /** This month (0 = Jan): its label is bold and later months are muted. */
  currentMonthIndex?: number;
};

/** Category key for the yearly chart, in its stacking order. */
export const AnnualExpensesLegend: React.FC<{
  data: AnnualPoint[];
  style?: StyleProp<ViewStyle>;
}> = ({ data, style }) => {
  const { theme } = useTheme();
  const styles = useMemo(() => createStyles(theme), [theme]);
  const types = useMemo(() => getExpenseTypeOrder(data), [data]);
  if (types.length === 0) return null;
  return (
    <View style={[styles.legend, style]}>
      {types.map(type => (
        <View key={type} style={styles.legendItem}>
          <View
            style={[
              styles.legendDot,
              {
                backgroundColor: getExpenseCategoryColor(
                  type,
                  theme.colors.textSecondary,
                ),
              },
            ]}
          />
          <Text style={styles.legendLabel}>{capitalizeFirstLetter(type)}</Text>
        </View>
      ))}
    </View>
  );
};

const AnnualExpensesBarChart: React.FC<Props> = ({
  data,
  currentMonthIndex,
}) => {
  const { theme } = useTheme();
  const styles = useMemo(() => createStyles(theme), [theme]);
  const [containerW, setContainerW] = useState<number | null>(null);
  // Wide sizes only when there is room, so a narrow web window keeps the phone
  // sizes. Phone margins are tight: each month needs ~23px for "Jan".
  const roomy = Platform.OS === 'web' && (containerW ?? 0) >= s(560);
  const yAxisLabelW = s(roomy ? 56 : 32);
  const chartWidth = useMemo(() => {
    const screenWidth = Dimensions.get('window').width;
    const fallback = screenWidth - s(32);
    const measured = containerW
      ? containerW - yAxisLabelW - s(8)
      : fallback - yAxisLabelW;
    return Math.max(measured, s(140));
  }, [containerW, yAxisLabelW]);
  const initialSpacing = s(6);
  const pointerLabelW = s(150);
  const endSpacing = s(8);
  const plotHeight = v(200);

  const typeOrder = useMemo(() => getExpenseTypeOrder(data), [data]);

  // Same colour per category as the donut and the Latest Expenses rows.
  const typeColors = useMemo(() => {
    const map: Record<string, string> = {};
    typeOrder.forEach(t => {
      map[t] = getExpenseCategoryColor(t, theme.colors.textSecondary);
    });
    return map;
  }, [typeOrder, theme.colors.textSecondary]);

  const { barWidth, spacing } = useMemo(() => {
    const n = Math.max((data || []).length, 1);
    const spacingMin = s(roomy ? 20 : 8);
    const barMin = s(roomy ? 16 : 10);
    const barMax = s(roomy ? 28 : 16);
    const available = Math.max(chartWidth - initialSpacing - endSpacing, 1);
    let computedBar = Math.min(
      barMax,
      Math.max(barMin, (available - spacingMin * Math.max(n - 1, 0)) / n),
    );
    let computedSpacing = n > 1 ? (available - computedBar * n) / (n - 1) : 0;
    if (computedSpacing < spacingMin) {
      computedSpacing = Math.max(
        0,
        (available - computedBar * n) / Math.max(n - 1, 1),
      );
    }
    return { barWidth: computedBar, spacing: computedSpacing };
  }, [data, chartWidth, initialSpacing, endSpacing, roomy]);

  // Round steps (250K, 500K …) instead of fifths of the tallest bar.
  const axis = useMemo(() => {
    const barTotals = (data || []).map(p =>
      typeOrder.reduce(
        (acc, t) => acc + Number(p?.totalsByType?.[t] || 0),
        0,
      ),
    );
    return getNiceAxis(Math.max(0, ...barTotals), 4);
  }, [data, typeOrder]);

  const shouldRotateX = useMemo(
    () => (data || []).some(d => String(d.month || '').length >= 5),
    [data],
  );

  const stackedData = useMemo(() => {
    const hasCurrentMonth = typeof currentMonthIndex === 'number';
    return (data || []).map((point, index) => {
      const stacks = typeOrder.map(t => ({
        value: Number(point?.totalsByType?.[t] || 0),
        color: typeColors[t],
      }));
      const labelTextStyle =
        hasCurrentMonth && index === currentMonthIndex
          ? styles.currentMonthLabel
          : hasCurrentMonth && index > (currentMonthIndex as number)
            ? styles.futureMonthLabel
            : undefined;
      return {
        label: point.month,
        stacks,
        ...(labelTextStyle
          ? { labelTextStyle: [styles.monthLabel, labelTextStyle] }
          : null),
      } as any;
    });
  }, [data, typeOrder, typeColors, currentMonthIndex, styles]);

  const tooltipBoundaryWidth = chartWidth;
  const isEmpty = (stackedData?.length || 0) === 0;
  const tooltipHeight = estimateChartTooltipHeight(
    Math.max(typeOrder.length, 1),
  );

  const shouldRenderChart = (data || []).length > 0 && typeOrder.length > 0;

  return (
    <View
      style={styles.container}
      onLayout={e => setContainerW(e.nativeEvent.layout.width)}>
      {!shouldRenderChart ? (
        <Text style={styles.emptyText}>No data in this time period</Text>
      ) : (
        <BarChart
          stackData={stackedData}
          width={chartWidth}
          xAxisLength={chartWidth}
          rulesLength={chartWidth}
          endSpacing={endSpacing}
          initialSpacing={initialSpacing}
          spacing={spacing}
          barWidth={barWidth}
          disableScroll
          yAxisLabelWidth={yAxisLabelW}
          maxValue={axis.maxValue}
          noOfSections={axis.noOfSections}
          stepValue={axis.stepValue}
          labelWidth={s(36)}
          xAxisLabelsHeight={30}
          height={plotHeight}
          rulesType="dashed"
          rulesColor={theme.colors.border}
          dashWidth={s(4)}
          dashGap={s(4)}
          yAxisThickness={0}
          xAxisThickness={1}
          xAxisColor={theme.colors.borderGray200}
          stackBorderTopLeftRadius={s(3)}
          stackBorderTopRightRadius={s(3)}
          yAxisTextStyle={styles.axisLabel}
          xAxisLabelTextStyle={styles.monthLabel}
          isAnimated
          animationDuration={600}
          rotateLabel={shouldRotateX}
          labelsExtraHeight={shouldRotateX ? v(18) : 0}
          xAxisTextNumberOfLines={1}
          formatYLabel={label =>
            formatNiceAxisTick(Number(label || '0'), axis.stepValue)
          }
          renderTooltip={
            isEmpty
              ? undefined
              : (item: any, index: number) => {
                  const safeIndex = Math.max(
                    0,
                    Math.min(index, Math.max(stackedData.length - 1, 0)),
                  );
                  const label = String(item?.label ?? '');
                  const stacks = (item?.stacks ?? []) as Array<{
                    value: number;
                    color: string;
                  }>;
                  const pointX = getBarChartPointX({
                    index: safeIndex,
                    initialSpacing,
                    barWidth,
                    spacing,
                  });
                  const stackTotal = stacks.reduce(
                    (sum, stack) => sum + Number(stack?.value ?? 0),
                    0,
                  );
                  const barHeight = getBarHeightFromValue(
                    stackTotal,
                    axis.maxValue,
                    plotHeight,
                  );
                  const translateY = getBarChartTooltipTranslateY({
                    barHeight,
                    plotHeight,
                    tooltipHeight,
                    gap: s(8),
                  });
                  // Only the categories that month spent on, named as in the legend.
                  const tooltipItems = typeOrder
                    .map((t, idx) => ({
                      color: typeColors[t],
                      label: capitalizeFirstLetter(t),
                      value: Number(stacks[idx]?.value || 0),
                      formatter: (n: number) => formatMoney(n),
                    }))
                    .filter(it => it.value > 0);

                  return (
                    <PositionedChartTooltip
                      pointX={pointX}
                      tooltipWidth={pointerLabelW}
                      boundaryWidth={tooltipBoundaryWidth}
                      translateY={translateY}>
                      {/* gifted-charts anchors a stacked bar's tooltip at the
                          bar's left edge; the helper centres on the bar. */}
                      <View
                        style={{
                          width: pointerLabelW,
                          marginLeft: barWidth / 2 - pointerLabelW / 2,
                        }}>
                        <ChartTooltip
                          header={label}
                          items={tooltipItems}
                          note={
                            tooltipItems.length === 0 ? 'No expenses' : undefined
                          }
                        />
                      </View>
                    </PositionedChartTooltip>
                  );
                }
          }
        />
      )}
    </View>
  );
};

export default AnnualExpensesBarChart;

const createStyles = (theme: any) =>
  StyleSheet.create({
    container: {
      paddingTop: v(8),
    },
    emptyText: {
      color: theme.colors.textSecondary,
      fontFamily: PoppinsFonts.regular,
      fontSize: fs(12),
      paddingVertical: v(24),
      textAlign: 'center',
    },
    axisLabel: {
      color: theme.colors.textSecondary,
      fontFamily: GTFonts.regular,
      fontSize: fs(11),
    },
    // System font, as before: the library sizes each label to its bar plus
    // the gap (about 24px on phones), which the wider GT face overflows.
    monthLabel: {
      color: theme.colors.textSecondary,
      fontSize: fs(10),
      textAlign: 'center',
    },
    currentMonthLabel: {
      color: theme.colors.text,
      fontWeight: '700',
    },
    futureMonthLabel: {
      color: theme.colors.textTertiary,
    },
    legend: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      columnGap: s(12),
      rowGap: v(6),
    },
    legendItem: {
      flexDirection: 'row',
      alignItems: 'center',
      columnGap: s(6),
    },
    legendDot: {
      width: s(8),
      height: s(8),
      borderRadius: s(4),
    },
    legendLabel: {
      color: theme.colors.textSecondary,
      fontFamily: GTFonts.regular,
      fontSize: fs(12),
      lineHeight: fs(16),
    },
  });
